import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { STATUS_COLOR } from "../lib/constants";

const BTN = {
  primary: "bg-sky-500 hover:bg-sky-600 active:bg-sky-700 text-white shadow-md shadow-sky-500/20",
  secondary: "bg-white hover:bg-slate-100 text-slate-800 border border-slate-300",
  danger: "bg-rose-600 hover:bg-rose-700 text-white",
  success: "bg-emerald-600 hover:bg-emerald-700 text-white",
  dark: "bg-slate-900 hover:bg-slate-800 text-white",
  ghost: "bg-transparent hover:bg-slate-100 text-slate-700",
};

export function Btn({ variant = "primary", loading, className = "", children, ...p }) {
  return (
    <button
      {...p}
      disabled={loading || p.disabled}
      className={`min-h-[48px] px-5 rounded-xl font-bold text-base inline-flex items-center justify-center gap-2 transition-[background-color,transform] duration-150 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none ${BTN[variant]} ${className}`}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

const INPUT = "w-full min-h-[48px] px-3.5 rounded-xl bg-white border border-slate-300 text-slate-900 text-base focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 placeholder:text-slate-400";

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      {label && <span className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1.5">{label}</span>}
      {children}
    </label>
  );
}

export const Input = ({ className = "", ...p }) => <input {...p} className={`${INPUT} ${className}`} />;
export const Textarea = ({ className = "", rows = 3, ...p }) => <textarea rows={rows} {...p} className={`${INPUT} py-3 ${className}`} />;

export function Select({ options, placeholder, className = "", value, onChange, required, disabled, name, id, ...p }) {
  const testid = p["data-testid"];
  return (
    <select value={value} onChange={onChange} required={required} disabled={disabled} name={name} id={id} data-testid={testid} className={`${INPUT} ${className}`}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const [v, l] = Array.isArray(o) ? o : [o, o];
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

export const Card = ({ className = "", children, ...p }) => (
  <div {...p} className={`bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm ${className}`}>{children}</div>
);

export function PageHead({ title, sub, action }) {
  return (
    <div className="flex items-end justify-between gap-3 fade-up">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export const StatusBadge = ({ status, ...p }) => (
  <span {...p} className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${STATUS_COLOR[status] || "bg-slate-200"}`}>
    {status}
  </span>
);

export const Empty = ({ text, ...p }) => (
  <div {...p} className="text-center py-12 px-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-500">{text}</div>
);

export const Loading = () => (
  <div className="flex justify-center py-16" data-testid="loading"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
);

export function DataList({ data, emptyText, emptyTestId, children }) {
  if (!data) return <Loading />;
  if (data.length === 0) return <Empty text={emptyText} data-testid={emptyTestId} />;
  return children;
}

export const Overline = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">{children}</div>
);

const OTHER = "__other__";

export function ChoiceInput({ options, value, onChange, placeholder, required, testid }) {
  const known = options.includes(value);
  const [manual, setManual] = useState(!!value && !known);
  const showManual = manual || (!!value && !known);
  const opts = useMemo(() => [...options.map((o) => [o, o]), [OTHER, "Lainnya (isi manual)..."]], [options]);
  return (
    <div className="space-y-2">
      <Select value={showManual ? OTHER : value} required={required && !showManual} data-testid={`${testid}-select`}
        options={opts} placeholder="- Pilih -"
        onChange={(e) => { const v = e.target.value; if (v === OTHER) { setManual(true); onChange(""); } else { setManual(false); onChange(v); } }} />
      {showManual && <Input autoFocus required={required} value={value} placeholder={placeholder || "Ketik manual"} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-manual`} />}
    </div>
  );
}
